import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import { PriceConverter } from '@/components/molecules/price-converter';
import { CURRENCIES, UNITS } from '@/lib/constants/form-options';
import { formatPrice } from '@/lib/utils';
import type { CurrencyType } from '@/lib/utils/price.utils';
import type { CreateProductFormData, UpdateProductFormData, EarningsEstimation } from '../types/product-form.types';

interface ProductPricingProps<T extends CreateProductFormData | UpdateProductFormData> {
  form: UseFormReturn<T>;
  showDiscountPrice?: boolean;
  showPriceConverter?: boolean;
  earningsEstimation?: EarningsEstimation | null;
  onCurrencyChange?: (currency: T['currency']) => void;
}

export function ProductPricing<T extends CreateProductFormData | UpdateProductFormData>({
  form,
  showDiscountPrice = false,
  showPriceConverter = true,
  earningsEstimation,
  onCurrencyChange,
}: ProductPricingProps<T>) {
  const { t } = useTranslation();
  const formValues = form.watch();

  // Default currency to prevent formatPrice errors
  const currency = formValues.currency || 'european_currency';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.pricing')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={'price' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.price')} *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showDiscountPrice ? (
            <FormField
              control={form.control}
              name={'discountPrice' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.comparePrice')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.valueAsNumber;
                        field.onChange(isNaN(val) ? undefined : val);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name={'quantity' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.stock')} *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={'currency' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.currency')} *</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    onCurrencyChange?.(value as T['currency']);
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {showDiscountPrice ? (
            <FormField
              control={form.control}
              name={'quantity' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.stock')} *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name={'unit' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.unit')} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {UNITS.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {t(unit.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {showDiscountPrice && (
          <FormField
            control={form.control}
            name={'unit' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.unit')} *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {UNITS.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {t(unit.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Price Converter */}
        {showPriceConverter && formValues.price > 0 && currency && (
          <PriceConverter
            price={formValues.price}
            sourceCurrency={currency as CurrencyType}
            compact
          />
        )}

        {/* Earnings Estimation */}
        {earningsEstimation && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
            <h4 className="mb-3 flex items-center gap-2 font-semibold text-green-800 dark:text-green-200">
              <span>💰</span>
              {t('products.earningsEstimation', 'Estimation de vos gains')}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('products.salePrice', 'Prix de vente')} :</span>
                <span className="font-medium">
                  {formatPrice(earningsEstimation.price, currency)}
                </span>
              </div>
              <div className="flex justify-between text-orange-600 dark:text-orange-400">
                <span>{t('products.commission', 'Commission')} ({earningsEstimation.commissionRate}%) :</span>
                <span>- {formatPrice(earningsEstimation.commissionAmount, currency)}</span>
              </div>
              <div className="border-t border-green-300 pt-2 dark:border-green-700">
                <div className="flex justify-between font-semibold text-green-700 dark:text-green-300">
                  <span>{t('products.youWillReceive', 'Vous recevrez')} :</span>
                  <span>{formatPrice(earningsEstimation.netAmount, currency)}</span>
                </div>
              </div>

              {/* Better plans comparison */}
              {earningsEstimation.betterPlans.length > 0 && (
                <div className="mt-3 border-t border-green-300 pt-3 dark:border-green-700">
                  <p className="mb-2 text-xs text-muted-foreground">
                    {t('products.comparePlans', 'Comparez avec d\'autres abonnements')} :
                  </p>
                  {earningsEstimation.betterPlans.map((plan) => (
                    <div key={plan.name} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {plan.name} ({plan.commissionRate}%) :
                      </span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {formatPrice(plan.netAmount, currency)}
                        <span className="ml-1 text-emerald-600">
                          (+{formatPrice(plan.savings, currency)})
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
